import type {
  DetailActivity,
  DetailPhase,
  ProjectDetail as ProjectDetailType,
} from "../types";

/* Delt logikk for Prosjektreisen: XP, faser og del-faser (1.1, 1.2 …). */

export const ACTIVITY_XP = 10;
export const MILESTONE_XP = 50;
export const XP_PER_LEVEL = 500;

export const ACT_STATUSES = [
  { value: "IkkeStartet", label: "Ikke startet" },
  { value: "Pågår", label: "Pågår" },
  { value: "Fullført", label: "Fullført" },
  { value: "IkkeRelevant", label: "Ikke relevant" },
] as const;

export const MS_STATUSES = [
  { value: "Pending", label: "Venter" },
  { value: "Approved", label: "Godkjent" },
  { value: "Delayed", label: "Forsinket" },
] as const;

/** Egen farge per fase — dempet, men distinkt, i tråd med GBs varme palett. */
// CSS-variabler (index.css): fargene følger paletten (Brun gir jordtoner).
// Brukes bare som CSS-verdier (--pc/--pcSoft), ikke som hex i JS.
const kat = (tone: string) => ({ main: `var(--kat-${tone})`, soft: `var(--kat-${tone}-soft)` });
export const PHASE_COLORS: Record<string, { main: string; soft: string }> = {
  AKKV: kat("amber"),
  REG: kat("teal"),
  SALG: kat("magenta"),
  FORP: kat("blue"),
  GJEN: kat("violet"),
  ETTER: kat("grey"),
};

export const PHASE_META: Record<
  string,
  { num: number; blurb: string; tip: string }
> = {
  AKKV: { num: 1, blurb: "Sikre tomten og legg grunnlaget.", tip: "Har du husket å laste opp kjøpekontrakten i dokumentarkivet?" },
  REG: { num: 2, blurb: "Fra idé til vedtatt reguleringsplan.", tip: "Har du husket å sette frist for høringsinnspill?" },
  SALG: { num: 3, blurb: "Boligene ut i markedet.", tip: "Har du husket å publisere prospekt på nettsiden?" },
  FORP: { num: 4, blurb: "Tegninger, budsjett og rammesøknad.", tip: "Har du husket å avstemme kalkylen mot bizview før rammesøknad?" },
  GJEN: { num: 5, blurb: "Fra spadetak til ferdigattest.", tip: "Har du husket å planlegge overtakelsesbefaringene?" },
  ETTER: { num: 6, blurb: "Overtakelse og ettermarked.", tip: "Har du husket å registrere reklamasjonsfrister?" },
};

/** Tips-bank per fase — hentet fra prosjektmodellen (kjøreboka A-Å). */
export const TIPS_BY_PHASE: Record<string, string[]> = {
  AKKV: [
    "Legg kjøpekontrakten og DD-rapporten i prosjektmappen med én gang.",
    "Avklar eierandel og partnerfordeling før oppgjør.",
    "Involver økonomi tidlig — en god kalkyle nå sparer tid i forprosjektet.",
    "Sørg for ryddig handover til prosjektorganisasjonen.",
  ],
  REG: [
    "Tidlig dialog med kommunen reduserer risiko for omkamper senere.",
    "Dokumentér alle medvirkningsinnspill — det styrker planforslaget.",
    "Sett realistiske frister for høring; offentlige prosesser tar tid.",
    "En komplett innsending gir raskere 1. gangs behandling.",
  ],
  SALG: [
    "Godt salgsmateriell selger raskere — invester i visualiseringene.",
    "Følg opp interessenter tett; de fleste salg skjer i oppfølgingen.",
    "Hold prislisten oppdatert mot markedet underveis.",
    "Koble marked inn i byggherreprogrammet tidlig.",
  ],
  FORP: [
    "Avstem kalkylen mot bizview før rammesøknad sendes.",
    "Lås romprogram og materialvalg tidlig for å unngå endringskostnader.",
    "En komplett rammesøknad gir raskere saksbehandling.",
    "Få kontraheringen på plass før oppstart gjennomføring.",
  ],
  GJEN: [
    "Planlegg overtakelsesbefaringene i god tid før ferdigstillelse.",
    "Ukentlig fremdriftsmøte holder avvik små.",
    "Dokumentér endringer fortløpende — det letter sluttoppgjøret.",
    "Sikre ferdigattest før overlevering til kjøper.",
  ],
  ETTER: [
    "Registrer reklamasjonsfrister med én gang overtakelse er gjennomført.",
    "En god prosjektevaluering gjør neste prosjekt bedre.",
    "Rask respons på reklamasjoner gir fornøyde kjøpere og godt omdømme.",
  ],
};

const GENERIC_TIPS = [
  "Oppdater status og kommentar så neste person forstår hvor saken står.",
  "Dokumentér beslutninger fortløpende — det letter revisjon og overlevering.",
  "Sjekk avhengigheter til andre aktiviteter før du setter i gang.",
];

const CHEERS = ["Bra jobba!", "Nydelig!", "Ett steg nærmere!", "Stå på!", "Sterkt!"];

function pick<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/** Et relevant tips for fasen (blander inn generelle tips). */
export function pickTip(phaseCode: string): string {
  const pool = [...(TIPS_BY_PHASE[phaseCode] ?? []), ...GENERIC_TIPS];
  return pick(pool);
}

export function pickCheer(): string {
  return pick(CHEERS);
}

const tipDateFmt = new Intl.DateTimeFormat("nb-NO", {
  day: "2-digit",
  month: "short",
});

/**
 * Situasjons- og aktivitets-relevant tips for prosjektlederen. Prioriterer
 * konkret handling: relevante dokumenter → frist → manglende ansvarlig →
 * fase-tips som fallback.
 */
export function activityTip(
  a: DetailActivity | null | undefined,
  phaseCode: string
): string {
  if (!a) return "Alt i denne fasen er i mål – sterkt jobbet!";

  if (a.relevantDocuments && a.relevantDocuments.trim()) {
    return `Har du husket å laste opp: ${a.relevantDocuments.trim()}?`;
  }

  if (a.deadline) {
    const d = new Date(a.deadline);
    if (!Number.isNaN(d.getTime())) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const diff = Math.round((d.getTime() - today.getTime()) / 86_400_000);
      if (diff < 0)
        return `«${a.activityDescription}» hadde frist ${tipDateFmt.format(d)} – følg opp?`;
      if (diff === 0) return `«${a.activityDescription}» har frist i dag.`;
      if (diff <= 7)
        return `«${a.activityDescription}» har frist om ${diff} ${
          diff === 1 ? "dag" : "dager"
        }.`;
    }
  }

  if (!a.responsible || !a.responsible.trim()) {
    return `Ingen ansvarlig er satt på «${a.activityDescription}» – hvem tar den?`;
  }

  return pickTip(phaseCode);
}

export const toDateInput = (v: string | null) => (v ? v.slice(0, 10) : "");
export const isActDone = (a: DetailActivity) => a.status === "Fullført";
export const isActSkipped = (a: DetailActivity) => a.status === "IkkeRelevant";

export type SubPhase = {
  label: string;
  num: string | null;
  name: string;
  activities: DetailActivity[];
  done: number;
  total: number;
};

/** Splitt "1.1 - Søkfasen" → { num: "1.1", name: "Søkfasen" }. */
export function parseSubPhase(label: string | null): {
  num: string | null;
  name: string;
} {
  if (!label) return { num: null, name: "Øvrig" };
  const m = label.match(/^(\d+(?:\.\d+)?)\s*[-–]\s*(.*)$/);
  if (m) return { num: m[1], name: m[2].trim() };
  return { num: null, name: label };
}

/** Grupper en fases aktiviteter i del-faser, sortert på nummer. */
export function groupSubPhases(phase: DetailPhase): SubPhase[] {
  const map = new Map<string, DetailActivity[]>();
  for (const a of phase.activities) {
    const key = a.subPhaseLabel ?? "Øvrig";
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(a);
  }
  const groups: SubPhase[] = [...map.entries()].map(([label, activities]) => {
    const { num, name } = parseSubPhase(label);
    return {
      label,
      num,
      name,
      activities,
      done: activities.filter(isActDone).length,
      total: activities.length,
    };
  });
  groups.sort((a, b) => (parseFloat(a.num ?? "999") || 999) - (parseFloat(b.num ?? "999") || 999));
  return groups;
}

export type ProjectStats = ReturnType<typeof computeStats>;

export function computeStats(project: ProjectDetailType) {
  const phases = project.phases;
  const completedActivities = phases.reduce((s, ph) => s + ph.completedCount, 0);
  const totalActivities = phases.reduce((s, ph) => s + ph.totalCount, 0);
  const approvedMs = project.milestones.filter((m) => m.status === "Approved").length;
  const xp = completedActivities * ACTIVITY_XP + approvedMs * MILESTONE_XP;
  const level = Math.floor(xp / XP_PER_LEVEL) + 1;
  const levelPct = Math.round(((xp % XP_PER_LEVEL) / XP_PER_LEVEL) * 100);
  const overallPct = totalActivities
    ? Math.round((completedActivities / totalActivities) * 100)
    : 0;
  return {
    completedActivities,
    totalActivities,
    approvedMs,
    totalMs: project.milestones.length,
    xp,
    level,
    levelPct,
    overallPct,
  };
}

export const phaseIsDone = (ph: DetailPhase) =>
  ph.totalCount > 0 && ph.completedCount >= ph.totalCount;

// ---- immutable oppdateringer på ProjectDetail ----
export function withActivity(
  p: ProjectDetailType,
  id: number,
  patch: Partial<DetailActivity>
): ProjectDetailType {
  return {
    ...p,
    phases: p.phases.map((ph) => {
      if (!ph.activities.some((a) => a.projectActivityId === id)) return ph;
      const activities = ph.activities.map((a) =>
        a.projectActivityId === id ? { ...a, ...patch } : a
      );
      return {
        ...ph,
        activities,
        completedCount: activities.filter(isActDone).length,
      };
    }),
  };
}

export function withNewActivity(
  p: ProjectDetailType,
  phaseId: number,
  a: DetailActivity
): ProjectDetailType {
  return {
    ...p,
    phases: p.phases.map((ph) =>
      ph.phaseId === phaseId
        ? { ...ph, activities: [...ph.activities, a], totalCount: ph.totalCount + 1 }
        : ph
    ),
  };
}

export function withMilestone(
  p: ProjectDetailType,
  id: number,
  patch: Partial<import("../types").DetailMilestone>
): ProjectDetailType {
  return {
    ...p,
    milestones: p.milestones.map((m) =>
      m.milestoneId === id ? { ...m, ...patch } : m
    ),
  };
}
